import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import App from './App';
import { theme } from './theme';
import { showErrorNotification, extractErrorMessage } from './utils/notifications';
import './index.css';
import './styles/animations.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/charts/styles.css';

// Track shown errors to avoid duplicate notifications
const shownErrorKeys = new Set<string>();

// Create query cache with global error handling
const queryCache = new QueryCache({
  onError: (error, query) => {
    // Skip showing notification for queries that explicitly opt out
    if (query.meta?.skipErrorNotification) {
      return;
    }
    
    // Skip 401 errors (handled by api.ts redirect)
    const errorMessage = extractErrorMessage(error);
    if (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) {
      return;
    }
    
    // Create a unique key for this error to avoid duplicates
    const queryKey = Array.isArray(query.queryKey) ? query.queryKey.join('-') : String(query.queryKey);
    const errorKey = `${queryKey}-${errorMessage}`;
    
    // Only show notification if we haven't shown this exact error recently
    if (!shownErrorKeys.has(errorKey)) {
      shownErrorKeys.add(errorKey);
      
      // Clear the key after 5 seconds to allow showing the same error again if it persists
      setTimeout(() => {
        shownErrorKeys.delete(errorKey);
      }, 5000);
      
      // Extract query name from queryKey for better error messages
      const queryName = Array.isArray(query.queryKey) 
        ? (typeof query.queryKey[0] === 'string' ? query.queryKey[0] : 'data')
        : 'data';
      
      showErrorNotification(error, {
        title: `Failed to load ${queryName}`,
        id: `query-error-${Date.now()}-${Math.random()}`,
      });
    }
  },
});

// Create mutation cache with global error handling
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    // Skip showing notification for mutations that explicitly opt out
    if (mutation.options.meta?.skipErrorNotification) {
      return;
    }
    
    // Create a unique key for this error
    const mutationKey = mutation.options.mutationKey 
      ? (Array.isArray(mutation.options.mutationKey) ? mutation.options.mutationKey.join('-') : String(mutation.options.mutationKey))
      : 'unknown';
    const errorMessage = extractErrorMessage(error);
    const errorKey = `mutation-${mutationKey}-${errorMessage}`;
    
    if (!shownErrorKeys.has(errorKey)) {
      shownErrorKeys.add(errorKey);
      
      setTimeout(() => {
        shownErrorKeys.delete(errorKey);
      }, 5000);
      
      const mutationName = mutation.options.mutationKey && Array.isArray(mutation.options.mutationKey)
        ? (typeof mutation.options.mutationKey[0] === 'string' ? mutation.options.mutationKey[0] : 'operation')
        : 'operation';
      
      showErrorNotification(error, {
        title: `Failed to ${mutationName}`,
        id: `mutation-error-${Date.now()}-${Math.random()}`,
      });
    }
  },
});

// Create a client with global error handling
const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Global defaults for all queries
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false, // Disable automatic retries - show errors immediately
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Global defaults for all mutations
      retry: false, // Disable automatic retries - show errors immediately
    },
  },
});

// Get initial color scheme from localStorage or default to dark
const getInitialColorScheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem('mantine-color-scheme-value');
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return 'dark'; // Default to dark
};

// Global error handler to suppress browser extension errors
if (typeof window !== 'undefined') {
  const originalErrorHandler = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    // Suppress errors from browser extension content scripts
    if (
      typeof source === 'string' &&
      (source.includes('content_script') ||
        source.includes('extension://') ||
        (typeof message === 'string' &&
          (message.includes("Cannot read properties of undefined (reading 'control')") ||
            message.includes("reading 'control'"))))
    ) {
      // Suppress the error - it's from a browser extension, not our code
      return true;
    }
    // Call original error handler if it exists
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };

  // Also handle unhandled promise rejections from extensions
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (
      reason &&
      typeof reason === 'object' &&
      'message' in reason &&
      typeof reason.message === 'string' &&
      (reason.message.includes("Cannot read properties of undefined (reading 'control')") ||
        reason.message.includes("reading 'control'"))
    ) {
      // Suppress the error
      event.preventDefault();
    }
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <MantineProvider
      defaultColorScheme={getInitialColorScheme()}
      theme={theme}
    >
      <Notifications 
        position="top-right" 
        zIndex={1000}
        limit={5}
        autoClose={3000}
      />
      <ModalsProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </React.StrictMode>,
);
